<template>
	<Layout>
		<div class="dashboard-page">
			<div class="page-content">
				<div class="page-header">
					<div><h2>控制台</h2><p>你好，{{ displayName }}，这里是你的业务数据概览</p></div>
					<div class="update-time"><i class="el-icon-time"></i> 数据更新于 {{ currentTime }}</div>
				</div>

				<section class="dashboard-section">
					<div class="section-heading"><div><h3>资产与收益</h3><span>账户累计数据</span></div></div>
					<div class="asset-grid">
						<div v-for="item in assetItems" :key="item.key" class="asset-card" :class="item.className">
							<div class="asset-icon"><i :class="item.icon"></i></div>
							<div class="asset-content">
								<div class="asset-label">{{ item.label }}
									<el-tooltip :content="item.tip" placement="top"><i class="el-icon-question metric-help"></i></el-tooltip>
								</div>
								<div class="asset-value"><small v-if="item.currency">¥</small>{{ item.value }}</div>
							</div>
						</div>
					</div>
				</section>

				<section class="dashboard-section">
					<el-card class="order-card" shadow="never" v-loading="orderLoading">
						<div class="order-header">
							<div class="section-heading order-title"><div><h3>订单收益报表</h3><span>{{ orderRangeText }} 数据</span></div></div>
							<el-radio-group v-model="orderType" size="small" @change="handleOrderTypeChange">
								<el-radio-button label="1">我的订单</el-radio-button>
								<el-radio-button label="2">团队订单</el-radio-button>
							</el-radio-group>
						</div>
						<div class="time-filter-row">
							<!-- <span class="filter-label">时间范围</span> -->
							<el-radio-group v-model="orderPeriod" size="small" @change="handlePeriodChange">
								<el-radio-button label="today">今日</el-radio-button>
								<el-radio-button label="yesterday">昨日</el-radio-button>
								<el-radio-button label="seven">近7日</el-radio-button>
								<el-radio-button label="month">本月</el-radio-button>
								<el-radio-button label="lastMonth">上月</el-radio-button>
								<el-radio-button label="custom">自定义</el-radio-button>
							</el-radio-group>
							<el-date-picker v-if="orderPeriod === 'custom'" ref="orderDatePicker" v-model="customOrderRange"
								class="custom-date-picker" type="daterange" size="small" align="right" unlink-panels
								range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期"
								value-format="timestamp" :picker-options="orderPickerOptions" @change="handleCustomDateChange">
							</el-date-picker>
						</div>
						<div class="platform-filter">
							<el-tabs v-model="platformTab" @tab-click="handlePlatformChange">
								<el-tab-pane v-for="item in ENUM.PLAT_LIST" :key="item.value" :label="item.label" :name="item.value.toString()"></el-tab-pane>
							</el-tabs>
						</div>
						<div v-if="hasValidOrderRange" class="order-stat-grid">
							<div v-for="item in orderStatItems" :key="item.key" class="order-stat-item">
								<div class="order-stat-label">{{ item.label }}</div>
								<div class="order-stat-value" :class="item.className"><small v-if="item.currency">¥</small>{{ item.value }}</div>
							</div>
						</div>
						<div v-else class="date-empty"><i class="el-icon-date"></i> 请选择日期范围后查看订单统计</div>
					</el-card>
				</section>

				<section class="dashboard-section"><TeamFansOverview /></section>
			</div>
		</div>
	</Layout>
</template>

<script>
import Layout from '@/components/Layout/index'
import TeamFansOverview from '@/views/teamFans/components/TeamFansOverview'
import { getOrderStat } from '@/api/public'
import { ENUM } from '@/utils/enum'
import { formatTime, getDayRangeBySetHours } from '@/utils/timeTransfer'

export default {
	name: 'DashboardIndex',
	components: { Layout, TeamFansOverview },
	data() {
		return {
			ENUM,
			orderType: '1',
			platformTab: '0',
			orderPeriod: 'today',
			customOrderRange: [],
			customPickerStart: null,
			orderLoading: false,
			orderRequestSeq: 0,
			lastUpdatedAt: new Date(),
			orderStat: { total_num: 0, failure_num: 0, receive_num: 0, order_num: 0, payment_price: 0, estimated_effect: 0 }
		}
	},
	computed: {
		userInfo() { return this.$store.getters.info || {} },
		displayName() {
			const subAccount = this.userInfo.sub_account_info
			return (subAccount && subAccount.username) || this.userInfo.nickname || '欢迎回来'
		},
		currentTime() { return formatTime(this.lastUpdatedAt, 'Y-M-D h:m') },
		orderRangeText() {
			const range = this.getOrderDateRange()
			if (!range) return '请选择开始和结束日期'
			const startText = formatTime(new Date(range.start), 'Y-M-D')
			const endText = formatTime(new Date(range.end), 'Y-M-D')
			return startText === endText ? startText : `${startText} 至 ${endText}`
		},
		hasValidOrderRange() { return Boolean(this.getOrderDateRange()) },
		orderPickerOptions() {
			return {
				disabledDate: time => {
					const value = time.getTime()
					if (value > new Date().setHours(23, 59, 59, 999)) return true
					if (!this.customPickerStart) return false
					const minDate = this.addMonths(this.customPickerStart, -6).setHours(0, 0, 0, 0)
					const maxDate = this.addMonths(this.customPickerStart, 6).setHours(23, 59, 59, 999)
					return value < minDate || value > maxDate
				},
				onPick: ({ minDate, maxDate }) => {
					this.customPickerStart = minDate && !maxDate ? new Date(minDate) : null
				},
				shortcuts: [
					{ text: '最近三个月', onClick: picker => this.pickRecentMonths(picker, 3) },
					{ text: '最近六个月', onClick: picker => this.pickRecentMonths(picker, 6) }
				]
			}
		},
		assetItems() {
			return [
				{ key: 'credit', label: '可提现余额', value: this.formatMoney(this.userInfo.credit), currency: true, icon: 'el-icon-wallet', className: 'is-balance', tip: '当前可申请提现的账户余额' },
				{ key: 'credit_total', label: '累计收益', value: this.formatMoney(this.userInfo.credit_total), currency: true, icon: 'el-icon-data-line', className: 'is-income', tip: '账户历史累计获得的收益' },
				{ key: 'credit_withdraw_ing', label: '提现中金额', value: this.formatMoney(this.userInfo.credit_withdraw_ing), currency: true, icon: 'el-icon-refresh', className: 'is-pending', tip: '已申请提现、当前仍在处理中的金额' },
				{ key: 'order_num', label: '累计总订单', value: this.formatNumber(this.userInfo.order_num), icon: 'el-icon-tickets', className: 'is-order', tip: '账户历史累计订单数量' }
			]
		},
		orderStatItems() {
			return [
				{ key: 'total_num', label: '总订单', value: this.formatNumber(this.orderStat.total_num) },
				{ key: 'order_num', label: '有效订单', value: this.formatNumber(this.orderStat.order_num), className: 'is-success' },
				{ key: 'failure_num', label: '失效订单', value: this.formatNumber(this.orderStat.failure_num), className: 'is-danger' },
				{ key: 'receive_num', label: '已收货订单', value: this.formatNumber(this.orderStat.receive_num) },
				{ key: 'payment_price', label: '有效付款金额', value: this.formatMoney(this.orderStat.payment_price), currency: true },
				{ key: 'estimated_effect', label: '有效预估佣金', value: this.formatMoney(this.orderStat.estimated_effect), currency: true, className: 'is-income' }
			]
		}
	},
	mounted() {
		this.fetchOrderStatistics()
		this.$store.dispatch('user/getUserInfo').catch(() => {})
	},
	methods: {
		formatNumber(value) {
			const number = Number(value || 0)
			return Number.isFinite(number) ? number.toLocaleString('zh-CN') : '0'
		},
		formatMoney(value) {
			const number = Number(value || 0)
			return Number.isFinite(number) ? number.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'
		},
		handleOrderTypeChange() {
			this.fetchOrderStatistics()
		},
		handlePlatformChange(tab) {
			this.platformTab = tab.name
			this.fetchOrderStatistics()
		},
		handlePeriodChange(period) {
			if (period === 'custom') {
				this.customOrderRange = []
				this.orderLoading = false
				this.$nextTick(() => {
					if (this.$refs.orderDatePicker) this.$refs.orderDatePicker.focus()
				})
				return
			}
			this.customPickerStart = null
			this.fetchOrderStatistics()
		},
		handleCustomDateChange(range) {
			this.customPickerStart = null
			if (!range || range.length !== 2) return
			if (!this.isRangeWithinSixMonths(range[0], range[1])) {
				this.customOrderRange = []
				this.$message.warning('自定义时间范围最长支持6个月')
				return
			}
			this.fetchOrderStatistics()
		},
		addMonths(dateValue, amount) {
			const date = new Date(dateValue)
			const day = date.getDate()
			const result = new Date(date)
			result.setDate(1)
			result.setMonth(result.getMonth() + amount)
			const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate()
			result.setDate(Math.min(day, lastDay))
			return result
		},
		isRangeWithinSixMonths(startValue, endValue) {
			const start = new Date(startValue)
			const end = new Date(endValue)
			return end.getTime() <= this.addMonths(start, 6).setHours(23, 59, 59, 999)
		},
		pickRecentMonths(picker, months) {
			const end = new Date()
			const start = this.addMonths(end, -months)
			picker.$emit('pick', [start, end])
		},
		getOrderDateRange() {
			const now = new Date()
			let start = new Date(now)
			let end = new Date(now)
			switch (this.orderPeriod) {
				case 'yesterday':
					start.setDate(start.getDate() - 1)
					end = new Date(start)
					break
				case 'seven':
					start.setDate(start.getDate() - 6)
					break
				case 'month':
					start = new Date(now.getFullYear(), now.getMonth(), 1)
					break
				case 'lastMonth':
					start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
					end = new Date(now.getFullYear(), now.getMonth(), 0)
					break
				case 'custom':
					if (!this.customOrderRange || this.customOrderRange.length !== 2) return null
					start = new Date(this.customOrderRange[0])
					end = new Date(this.customOrderRange[1])
					break
				default:
					break
			}
			return getDayRangeBySetHours(start, end)
		},
		fetchOrderStatistics() {
			const dateRange = this.getOrderDateRange()
			if (!dateRange) return Promise.resolve()
			const requestSeq = ++this.orderRequestSeq
			this.orderLoading = true
			return getOrderStat({
				type_big: Number(this.platformTab),
				user_type: this.orderType === '1' ? 0 : 1,
				order_sn: '',
				pay_start: Math.floor(dateRange.start / 1000),
				pay_end: Math.floor(dateRange.end / 1000)
			}).then(res => {
				if (requestSeq !== this.orderRequestSeq) return
				const data = res && res.data ? res.data : {}
				this.orderStat = {
					total_num: data.total_num || 0,
					failure_num: data.failure_num || 0,
					receive_num: data.receive_num || 0,
					order_num: data.order_num || 0,
					payment_price: data.payment_price || 0,
					estimated_effect: data.estimated_effect || 0
				}
				this.lastUpdatedAt = new Date()
			}).catch(error => {
				if (requestSeq !== this.orderRequestSeq || (error && error.isAuthError)) return
				console.error('获取控制台订单统计失败:', error)
			}).finally(() => {
				if (requestSeq === this.orderRequestSeq) this.orderLoading = false
			})
		}
	}
}
</script>

<style lang="scss" scoped>
.dashboard-page { min-height: 100vh; padding: 24px; background: #f5f7fa; }
.page-content { width: 100%; margin: 0 auto; }
.page-header {
	display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 24px;
	h2 { margin: 0 0 8px; color: #303133; font-size: 24px; font-weight: 600; }
	p { margin: 0; color: #909399; font-size: 14px; }
	.update-time { color: #909399; font-size: 13px; }
}
.dashboard-section { margin-bottom: 20px; }
.section-heading {
	display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px;
	h3 { display: inline-block; margin: 0 10px 0 0; color: #303133; font-size: 17px; }
	span { color: #a1a6b0; font-size: 13px; }
}
.asset-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.asset-card {
	position: relative; display: flex; align-items: center; min-width: 0; padding: 22px; overflow: hidden; background: #fff; border: 1px solid #ebeef5; border-radius: 10px;
	&::after { position: absolute; right: 0; bottom: 0; width: 64px; height: 4px; border-radius: 4px 0 0; content: ''; }
	&.is-balance::after { background: #409eff; } &.is-income::after { background: #67c23a; } &.is-pending::after { background: #e6a23c; } &.is-order::after { background: #8b6cf6; }
	.asset-icon { display: flex; flex: 0 0 48px; align-items: center; justify-content: center; height: 48px; margin-right: 16px; color: #409eff; font-size: 22px; background: #ecf5ff; border-radius: 12px; }
	&.is-income .asset-icon { color: #67c23a; background: #f0f9eb; } &.is-pending .asset-icon { color: #e6a23c; background: #fdf6ec; } &.is-order .asset-icon { color: #8b6cf6; background: #f2efff; }
	.asset-content { min-width: 0; } .asset-label { margin-bottom: 8px; color: #909399; font-size: 13px; }
	.asset-value { overflow: hidden; color: #303133; font-family: 'customFont'; font-size: 26px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
	small { margin-right: 2px; font-size: 15px; font-weight: 500; }
}
.metric-help { margin-left: 3px; color: #c0c4cc; cursor: help; }
.order-card { border: 1px solid #ebeef5; border-radius: 10px; ::v-deep .el-card__body { padding: 20px 24px 24px; } }
.order-header { display: flex; align-items: center; justify-content: space-between; }
.order-title { margin-bottom: 0; }
.time-filter-row {
	display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin-top: 20px;
	.filter-label { color: #606266; font-size: 13px; font-weight: 500; }
	.custom-date-picker { width: 280px; }
	::v-deep .el-radio-group { display: flex; flex-wrap: wrap; row-gap: 8px; }
}
.platform-filter {
	min-width: 0; margin-top: 10px;
	::v-deep .el-tabs__header { margin: 0 0 20px; } ::v-deep .el-tabs__nav-wrap { overflow-x: auto; scrollbar-width: none; }
	::v-deep .el-tabs__nav-wrap::-webkit-scrollbar { display: none; } ::v-deep .el-tabs__nav-wrap::after { height: 1px; background: #ebeef5; }
	::v-deep .el-tabs__nav-scroll { overflow: visible; } ::v-deep .el-tabs__nav { display: flex; float: none; transform: none !important; }
	::v-deep .el-tabs__item { flex: 0 0 auto; height: 46px; padding: 0 16px; line-height: 46px; }
	::v-deep .el-tabs__nav-prev, ::v-deep .el-tabs__nav-next { display: none; }
}
.order-stat-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); }
.order-stat-item { min-width: 0; padding: 4px 20px; border-right: 1px solid #ebeef5; }
.order-stat-item:first-child { padding-left: 4px; } .order-stat-item:last-child { padding-right: 4px; border-right: 0; }
.order-stat-label { margin-bottom: 10px; color: #909399; font-size: 13px; }
.order-stat-value {
	overflow: hidden; color: #303133; font-size: 24px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap;
	&.is-success { color: #67c23a; } &.is-danger { color: #f56c6c; } &.is-income { color: #e6a23c; }
	small { margin-right: 2px; font-size: 14px; font-weight: 500; }
}
.date-empty { padding: 28px 0 10px; color: #909399; font-size: 14px; text-align: center; i { margin-right: 5px; } }
@media (max-width: 1200px) {
	.asset-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
	.order-stat-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); row-gap: 24px; }
	.order-stat-item:nth-child(3) { border-right: 0; }
}
@media (max-width: 768px) {
	.dashboard-page { padding: 14px; } .page-header { align-items: flex-start; } .update-time { display: none; }
	.asset-grid { grid-template-columns: 1fr; gap: 10px; } .asset-card { padding: 16px; }
	.order-header { align-items: flex-start; flex-direction: column; gap: 14px; }
	.time-filter-row { align-items: flex-start; flex-direction: column; }
	.time-filter-row .custom-date-picker { width: 100%; }
	.order-stat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); row-gap: 20px; }
	.order-stat-item { padding: 2px 12px; } .order-stat-item:nth-child(odd) { padding-left: 2px; border-right: 1px solid #ebeef5; } .order-stat-item:nth-child(even) { padding-right: 2px; border-right: 0; }
}
</style>
